import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, LogIn, Play, User, Flame, Plus, Equal, Wand2, Shirt, Image, Mail, CheckCircle, AlertCircle, Loader2, Globe, Menu, X, Store, Users, Check, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImprovedDemo from '../components/ui/ImprovedDemo';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import VideoModal from '../components/modals/VideoModal';

// Componente de formulario de contacto
const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://amcnvhzwxjfkmhsvvyey.supabase.co/functions/v1/contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nombre */}
          <div>
            <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.name')} *
            </label>
            <input
              type="text"
              id="nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder={t('contact.form.namePlaceholder')}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder={t('contact.form.emailPlaceholder')}
            />
          </div>
        </div>

        {/* Mensaje */}
        <div>
          <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
            {t('contact.form.message')} *
          </label>
          <textarea
            id="mensaje"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
            placeholder={t('contact.form.messagePlaceholder')}
          />
        </div>

        {/* Botón de envío */}
        <div className="text-center pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center space-x-2 mx-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('contact.form.sending')}</span>
              </>
            ) : (
              <>
                <Mail className="h-5 w-5" />
                <span>{t('contact.form.submit')}</span>
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-3">
            * {t('contact.form.required')}
          </p>
        </div>
      </form>

      {/* Mensajes de estado */}
      {submitStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3"
        >
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-green-800 font-medium">{t('contact.form.success')}</p>
            <p className="text-green-600 text-sm">{t('contact.form.successDesc')}</p>
          </div>
        </motion.div>
      )}

      {submitStatus === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3"
        >
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{t('contact.form.error')}</p>
            <p className="text-red-600 text-sm">{t('contact.form.errorDesc')}</p>
          </div>
        </motion.div>
      )}

      {/* Información de contacto */}
      <div className="mt-8 pt-8 border-t border-gray-200">
        <div className="text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-800 mb-1">{t('contact.form.email')}</h3>
          <p className="text-sm text-gray-600">{t('contact.email')}</p>
        </div>
      </div>
    </div>
  );
};

const LandingPage = ({ user, loading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Cerrar menú móvil al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Detectar scroll para efecto sticky
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  

  // Función para obtener la URL de la aplicación principal
  const getAppUrl = () => {
    // En desarrollo, usar localhost:3001
    // En producción, usar app.luxify.ai
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    return 'https://app.luxify.ai';
  };

  // Función para redirigir a registro
  const redirectToRegister = () => {
    window.location.href = `${getAppUrl()}/register`;
  };

  // Función para redirigir a login
  const redirectToLogin = () => {
    window.location.href = `${getAppUrl()}/login`;
  };

  // Función para smooth scroll a la sección de demo
  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo-section');
    if (demoSection) {
      const headerHeight = 80; // Altura aproximada del header sticky
      const elementPosition = demoSection.offsetTop;
      const offsetPosition = elementPosition - headerHeight;

      // Agregar efecto visual de resaltado
      demoSection.style.transition = 'box-shadow 0.3s ease-in-out';
      demoSection.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.3)';
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Quitar el efecto de resaltado después de 2 segundos
      setTimeout(() => {
        demoSection.style.boxShadow = '';
      }, 2000);
    }
  };


  // Función genérica para smooth scroll con efectos
  const scrollToSection = (sectionId, color = 'rgba(59, 130, 246, 0.3)') => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = 80;
      const elementPosition = section.offsetTop;
      const offsetPosition = elementPosition - headerHeight;

      // Agregar efecto visual de resaltado
      section.style.transition = 'box-shadow 0.3s ease-in-out';
      section.style.boxShadow = `0 0 0 4px ${color}`;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Quitar el efecto de resaltado después de 2 segundos
      setTimeout(() => {
        section.style.boxShadow = '';
      }, 2000);
    }
  };

  // Efecto typewriter
  useEffect(() => {
    // Textos para el efecto typewriter
    const typewriterTexts = [
      t('hero.typewriterTexts.0'),
      t('hero.typewriterTexts.1'),
      t('hero.typewriterTexts.2'),
      t('hero.typewriterTexts.3')
    ];
    
    const fullText = typewriterTexts[currentTextIndex];
    
    const timeout = setTimeout(() => {
      if (isDeleting) {
        setCurrentText(fullText.substring(0, currentText.length - 1));
      } else {
        setCurrentText(fullText.substring(0, currentText.length + 1));
      }
    }, isDeleting ? 30 : 60);

    if (!isDeleting && currentText === fullText) {
      setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && currentText === '') {
      setIsDeleting(false);
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % typewriterTexts.length);
    }

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentTextIndex]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-x-hidden">
      {/* Toolbar */}
      <div className={`bg-white/70 backdrop-blur-md border-b border-gray-200 fixed top-0 left-0 right-0 z-50 mobile-menu-container transition-all duration-300 ${
        isScrolled 
          ? 'shadow-lg bg-white/80 backdrop-blur-lg' 
          : 'shadow-sm'
      }`}>
        <div className={`container mx-auto px-4 transition-all duration-300 ${
          isScrolled ? 'py-3' : 'py-4'
        }`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo192.png" 
                alt="Luxify Logo" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Luxify
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Navegación por secciones - Solo texto */}
              <div className="flex items-center space-x-6">
                <button
                  onClick={() => scrollToSection('inicio', 'rgba(59, 130, 246, 0.3)')}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-300 text-sm font-medium"
                >
                  {t('navigation.home')}
                </button>
                <button
                  onClick={() => scrollToSection('como-funciona', 'rgba(147, 51, 234, 0.3)')}
                  className="text-gray-600 hover:text-purple-600 transition-colors duration-300 text-sm font-medium"
                >
                  {t('navigation.howItWorks')}
                </button>
                <button
                  onClick={() => scrollToSection('demo-section', 'rgba(34, 197, 94, 0.3)')}
                  className="text-gray-600 hover:text-green-600 transition-colors duration-300 text-sm font-medium"
                >
                  {t('navigation.demo')}
                </button>
                <button
                  onClick={() => scrollToSection('planes', 'rgba(168, 85, 247, 0.3)')}
                  className="text-gray-600 hover:text-purple-600 transition-colors duration-300 text-sm font-medium"
                >
                  {t('navigation.pricing')}
                </button>
              </div>
              
               {/* Botones de acción */}
               <div className="flex items-center space-x-3">
                 {loading ? (
                   <button
                     disabled
                     className="flex items-center space-x-2 bg-gray-400 text-white px-6 py-2 rounded-lg cursor-not-allowed text-sm font-semibold"
                   >
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                     <span>{t('footer.verifying')}</span>
                   </button>
                 ) : user ? (
                   <button
                     onClick={() => navigate('/dashboard')}
                     className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 text-sm font-semibold"
                   >
                     <User className="h-4 w-4" />
                     <span>{t('hero.cta.dashboard')}</span>
                   </button>
                 ) : (
                   <button
                     onClick={redirectToLogin}
                     className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 text-sm font-semibold"
                   >
                     <LogIn className="h-4 w-4" />
                     <span>{t('hero.cta.signIn')}</span>
                   </button>
                 )}
                 <LanguageSwitcher />
               </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 text-gray-700" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 pb-4 border-t border-gray-200"
            >
              <div className="flex flex-col space-y-4 pt-4">
                {/* Navigation Links */}
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => {
                      scrollToSection('inicio', 'rgba(59, 130, 246, 0.3)');
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left text-gray-600 hover:text-blue-600 transition-colors duration-300 text-sm font-medium py-2"
                  >
                    {t('navigation.home')}
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('como-funciona', 'rgba(147, 51, 234, 0.3)');
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left text-gray-600 hover:text-purple-600 transition-colors duration-300 text-sm font-medium py-2"
                  >
                    {t('navigation.howItWorks')}
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('demo-section', 'rgba(34, 197, 94, 0.3)');
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left text-gray-600 hover:text-green-600 transition-colors duration-300 text-sm font-medium py-2"
                  >
                    {t('navigation.demo')}
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('planes', 'rgba(168, 85, 247, 0.3)');
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left text-gray-600 hover:text-purple-600 transition-colors duration-300 text-sm font-medium py-2"
                  >
                    {t('navigation.pricing')}
                  </button>
                </div>

                {/* Language Switcher */}
                <div className="flex items-center justify-between py-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700 flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    Idioma
                  </span>
                  <LanguageSwitcher />
                </div>
                
                 {/* Action Buttons */}
                 <div className="flex flex-col space-y-3 pt-2">
                   {loading ? (
                     <button
                       disabled
                       className="w-full flex items-center justify-center space-x-2 bg-gray-400 text-white px-6 py-3 rounded-lg cursor-not-allowed text-sm font-semibold"
                     >
                       <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                       <span>{t('footer.verifying')}</span>
                     </button>
                   ) : user ? (
                     <button
                       onClick={() => {
                         navigate('/dashboard');
                         setIsMobileMenuOpen(false);
                       }}
                       className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-300 text-sm font-semibold"
                     >
                       <User className="h-4 w-4" />
                       <span>{t('hero.cta.dashboard')}</span>
                     </button>
                   ) : (
                     <button
                       onClick={() => {
                         redirectToLogin();
                         setIsMobileMenuOpen(false);
                       }}
                       className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-sm font-semibold"
                     >
                       <LogIn className="h-4 w-4" />
                       <span>{t('hero.cta.signIn')}</span>
                     </button>
                   )}
                 </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <section id="inicio" className="pt-20">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16 relative"
        >
          {/* Elementos decorativos de fondo */}
          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-80 h-80 sm:w-96 sm:h-96 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-10 right-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-r from-pink-400/30 to-rose-400/30 rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 left-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-cyan-400/30 to-blue-400/30 rounded-full blur-2xl"></div>
          
          {/* Badge destacado */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-xl"
          >
            <Sparkles className="h-4 w-4 animate-pulse" />
            <span>{t('hero.badge')}</span>
          </motion.div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-8 tracking-tight leading-tight min-h-[120px] flex items-center justify-center relative z-10">
            <span className="text-center whitespace-pre-line">
              {currentText}
              <span className="animate-pulse text-blue-600 ml-1">|</span>
            </span>
          </h1>
          
          {/* Descripción llamativa */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <p className="text-xl sm:text-2xl font-medium text-gray-700 mb-4 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-4 text-lg text-gray-600">
              <span className="flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-full shadow-md">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span>{t('hero.features.ai')}</span>
              </span>
              <span className="flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-full shadow-md">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                <span>{t('hero.features.instant')}</span>
              </span>
              <span className="flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-full shadow-md">
                <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                <span>{t('hero.features.easy')}</span>
              </span>
            </div>
          </motion.div>
          
          {/* Demo Visual Mejorado */}
          <div className="flex flex-col lg:flex-row justify-center items-center space-y-8 lg:space-y-0 lg:space-x-8 mb-16">
            {/* Paso 1: Foto del Cliente */}
            <motion.div 
              className="text-center group"
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ 
                scale: 1.05, 
                y: -15,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
            >
              <div className="relative">
                {/* Container simplificado */}
                <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                  {/* Neumorphism Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl shadow-[inset_-2px_-2px_10px_rgba(59,130,246,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(59,130,246,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                  
                  {/* Image Container */}
                  <div className="relative w-full h-full rounded-3xl overflow-hidden m-2">
                    <img 
                      src="/images/foto_cliente.png" 
                      alt="Foto del cliente" 
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                  
                </div>
                
                
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-3xl"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Image className="h-8 w-8 text-blue-600" />
                <span>{t('hero.steps.upload')}</span>
              </h3>
              <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('hero.steps.uploadDesc')}</p>
            </motion.div>
            
            {/* Signo + */}
            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_12px_40px_rgba(147,51,234,0.4),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 backdrop-blur-sm border border-white/20">
                <Plus className="h-10 w-10 text-white drop-shadow-sm" />
              </div>
            </motion.div>
            
            {/* Paso 2: Prenda del Catálogo */}
            <motion.div 
              className="text-center group"
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              whileHover={{ 
                scale: 1.05, 
                y: -15,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
            >
              <div className="relative">
                {/* Container simplificado */}
                <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                  {/* Neumorphism Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl shadow-[inset_-2px_-2px_10px_rgba(147,51,234,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(147,51,234,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                  
                  {/* Image Container */}
                  <div className="relative w-full h-full rounded-3xl overflow-hidden m-2">
                    <img 
                      src="/images/prenda_catalogo.png" 
                      alt="Prenda de catálogo" 
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                  
                </div>
                
                
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-3xl"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Shirt className="h-8 w-8 text-purple-600" />
                <span>{t('hero.steps.choose')}</span>
              </h3>
              <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('hero.steps.chooseDesc')}</p>
            </motion.div>
            
            {/* Signo = */}
            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.6 }}
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(236,72,153,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_12px_40px_rgba(236,72,153,0.4),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 backdrop-blur-sm border border-white/20">
                <Equal className="h-10 w-10 text-white drop-shadow-sm" />
              </div>
            </motion.div>
            
            {/* Paso 3: Resultado Virtual */}
            <motion.div 
              className="text-center group"
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.8 }}
              whileHover={{ 
                scale: 1.05, 
                y: -15,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
            >
              <div className="relative">
                {/* Container simplificado */}
                <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                  {/* Neumorphism Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl shadow-[inset_-2px_-2px_10px_rgba(34,197,94,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(34,197,94,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                  
                  {/* Image Container */}
                  <div className="relative w-full h-full rounded-3xl overflow-hidden m-2">
                    <img 
                      src="/images/resultado_virtual.png" 
                      alt="Resultado virtual" 
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                  
                </div>
                
                
                {/* IA Badge simplificado */}
                <div className="absolute top-4 left-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                  ✨ IA
                </div>
                
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-3xl"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-green-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Wand2 className="h-8 w-8 text-green-600" />
                <span>{t('hero.steps.result')}</span>
              </h3>
              <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('hero.steps.resultDesc')}</p>
            </motion.div>
          </div>
        </motion.div>
        </section>


        {/* Sección Llamativa para Usuarios */}
        <section className="w-full py-16 bg-gradient-to-r from-pink-50 via-purple-50 to-indigo-50 relative overflow-hidden rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center relative z-10"
            >
              {/* Elementos decorativos de fondo */}
              <div className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-br from-pink-200 to-purple-200 rounded-full opacity-30 -translate-y-16"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full opacity-30 translate-y-16"></div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
                  <Sparkles className="h-4 w-4" />
                  <span>{t('userSection.badge')}</span>
                </div>
                
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  {t('userSection.title')}
                </h2>
                
                <p className="text-xl sm:text-2xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
                  <span className="font-semibold text-purple-600">{t('userSection.subtitle')}</span> 
                  <br />
                  <span className="text-lg sm:text-xl text-gray-600 mt-2 block">
                    {t('userSection.description')}
                  </span>
                </p>
                
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <button
                    onClick={scrollToDemo}
                    className="group flex items-center space-x-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-4 rounded-2xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl text-lg font-semibold"
                  >
                    <Play className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <span>{t('userSection.ctaTryNow')}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                  
                  <button
                    onClick={() => scrollToSection('planes', 'rgba(168, 85, 247, 0.3)')}
                    className="group flex items-center space-x-3 bg-white text-purple-600 px-8 py-4 rounded-2xl hover:bg-purple-50 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg font-semibold border-2 border-purple-200 hover:border-purple-300"
                  >
                    <span>{t('userSection.ctaViewPlans')}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
                
                {/* Estadísticas rápidas */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">✨</div>
                    <p className="text-gray-600 font-medium">{t('userSection.stats.instantTryOn')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-pink-600 mb-2">🛍️</div>
                    <p className="text-gray-600 font-medium">{t('userSection.stats.buyWithConfidence')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-indigo-600 mb-2">💰</div>
                    <p className="text-gray-600 font-medium">{t('userSection.stats.saveMoney')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sección Llamativa para Vendedores */}
        <section className="w-full py-16 bg-gradient-to-r from-blue-50 via-cyan-50 to-teal-50 relative overflow-hidden rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center relative z-10"
            >
              {/* Elementos decorativos de fondo */}
              <div className="absolute top-0 right-1/4 w-32 h-32 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full opacity-30 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-teal-200 to-emerald-200 rounded-full opacity-30 translate-y-16"></div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
                  <Flame className="h-4 w-4" />
                  <span>{t('sellerSection.badge')}</span>
                </div>
                
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  {t('sellerSection.title')}
                </h2>
                
                <p className="text-xl sm:text-2xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
                  <span className="font-semibold text-blue-600">{t('sellerSection.subtitle')}</span> 
                  <br />
                  <span className="text-lg sm:text-xl text-gray-600 mt-2 block">
                    {t('sellerSection.description')}
                  </span>
                </p>
                
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <button
                    onClick={() => scrollToSection('planes', 'rgba(59, 130, 246, 0.3)')}
                    className="group flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-8 py-4 rounded-2xl hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl text-lg font-semibold"
                  >
                    <Sparkles className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <span>{t('sellerSection.ctaStartNow')}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                  
                  <button
                    onClick={scrollToDemo}
                    className="group flex items-center space-x-3 bg-white text-blue-600 px-8 py-4 rounded-2xl hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg font-semibold border-2 border-blue-200 hover:border-blue-300"
                  >
                    <Play className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <span>{t('sellerSection.ctaViewDemo')}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
                
                {/* Estadísticas rápidas */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">📈</div>
                    <p className="text-gray-600 font-medium">{t('sellerSection.stats.increaseSales')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-600 mb-2">🛒</div>
                    <p className="text-gray-600 font-medium">{t('sellerSection.stats.reduceReturns')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-teal-600 mb-2">💼</div>
                    <p className="text-gray-600 font-medium">{t('sellerSection.stats.professionalizeStore')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sección "¿Por qué elegirnos?" */}
        <section className="w-full py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  {t('whyChooseUs.title')}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {t('whyChooseUs.description')}
                </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
                {/* Ventajas para Vendedores */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
                >
                  {/* Decorative background */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16"></div>
                  
                  <div className="relative">
                    <div className="flex items-start mb-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-5 shadow-lg flex-shrink-0">
                        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div className="flex-1 flex flex-col justify-start">
                        <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{t('whyChooseUs.sellers.title')}</h3>
                        <p className="text-blue-600 font-medium text-sm lg:text-base">{t('whyChooseUs.sellers.subtitle')}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-7">
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">{t('whyChooseUs.sellers.onlineCatalog.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.sellers.onlineCatalog.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">{t('whyChooseUs.sellers.moreSales.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.sellers.moreSales.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">{t('whyChooseUs.sellers.futureFeatures.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.sellers.futureFeatures.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Ventajas para Usuarios */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
                >
                  {/* Decorative background */}
                  <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-green-100 to-blue-100 rounded-full -translate-y-16 -translate-x-16"></div>
                  
                  <div className="relative">
                    <div className="flex items-start mb-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mr-5 shadow-lg flex-shrink-0">
                        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 flex flex-col justify-start">
                        <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{t('whyChooseUs.users.title')}</h3>
                        <p className="text-green-600 font-medium text-sm lg:text-base">{t('whyChooseUs.users.subtitle')}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-7">
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-200">{t('whyChooseUs.users.tryBeforeBuy.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.users.tryBeforeBuy.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-200">{t('whyChooseUs.users.createOutfits.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.users.createOutfits.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-5 group">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200 mt-1">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-200">{t('whyChooseUs.users.saveMoney.title')}</h4>
                          <p className="text-gray-600 leading-relaxed text-sm lg:text-base">{t('whyChooseUs.users.saveMoney.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Sección "Mira los resultados" */}
        <section className="w-full py-20 bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  {t('resultsSection.title')}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {t('resultsSection.description')}
                </p>
              </motion.div>

              {/* Grid de imágenes del proceso */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-7xl mx-auto mb-16">
                {/* Imagen original */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-center"
                >
                  <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6 transform hover:scale-105 transition-all duration-300">
                    <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-6 shadow-lg">
                      <img
                        src="/images/brad_original.jpg"
                        alt="Brad Pitt original"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('resultsSection.originalImage.title')}</h3>
                    <p className="text-gray-600 text-lg">{t('resultsSection.originalImage.description')}</p>
                  </div>
                </motion.div>

                {/* Prenda del catálogo */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="text-center"
                >
                  <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6 transform hover:scale-105 transition-all duration-300">
                    <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-6 shadow-lg">
                      <img
                        src="/images/hat_2.jpg"
                        alt="Sombrero del catálogo"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('resultsSection.catalogItem.title')}</h3>
                    <p className="text-gray-600 text-lg">{t('resultsSection.catalogItem.description')}</p>
                  </div>
                </motion.div>

                {/* Resultado final */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="text-center"
                >
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl shadow-2xl p-8 mb-6 border-4 border-green-300 transform hover:scale-105 transition-all duration-300 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-400 to-blue-400 rounded-full -translate-y-10 translate-x-10 opacity-20"></div>
                    <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-6 shadow-lg relative">
                      <img
                        src="/images/brad_result.jpg"
                        alt="Resultado virtual"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('resultsSection.virtualResult.title')}</h3>
                    <p className="text-gray-600 text-lg mb-4">{t('resultsSection.virtualResult.description')}</p>
                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full text-sm font-bold shadow-lg">
                      {t('resultsSection.virtualResult.aiGenerated')}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Sección de video */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-center max-w-4xl mx-auto"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('resultsSection.videoTitle')}
                </h3>
                <p className="text-gray-600 mb-8">
                  {t('resultsSection.videoDescription')}
                </p>
                
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                    <video
                      controls
                      loop
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      poster="/images/brad_result.jpg"
                    >
                      <source src="/images/brad_hat.webm" type="video/webm" />
                      Tu navegador no soporta el elemento de video.
                    </video>
                  </div>
                  
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">
                      {t('resultsSection.videoDisclaimer')}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Sección Try on Everything */}
        <section className="w-full py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16 relative"
              >
                {/* Elementos decorativos de fondo */}
                <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-80 h-80 sm:w-96 sm:h-96 bg-gradient-to-r from-indigo-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
                <div className="absolute top-10 right-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-r from-pink-400/30 to-rose-400/30 rounded-full blur-2xl"></div>
                <div className="absolute bottom-10 left-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-cyan-400/30 to-blue-400/30 rounded-full blur-2xl"></div>
                
                {/* Badge destacado */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-xl"
                >
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span>{t('tryOnSection.badge')}</span>
                </motion.div>
                
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  {t('tryOnSection.title')}
                </h2>
                
                <p className="text-xl sm:text-2xl text-gray-700 mb-4 max-w-4xl mx-auto leading-relaxed">
                  <span className="font-semibold text-purple-600">{t('tryOnSection.subtitle')}</span>
                </p>
                
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {t('tryOnSection.description')}
                </p>
              </motion.div>

              {/* Demo del proceso con las 3 camisas */}
              <div className="flex flex-col lg:flex-row justify-center items-center space-y-8 lg:space-y-0 lg:space-x-8">
                {/* Paso 1: Camisa 1 - Foto Original */}
                <motion.div 
                  className="text-center group"
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -15,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <div className="relative">
                    <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-[2rem] shadow-[inset_-2px_-2px_10px_rgba(59,130,246,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(59,130,246,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                      
                      <div className="relative w-full h-full rounded-[2rem] overflow-hidden m-2">
                        <img 
                          src="/images/camisa_1.jpg" 
                          alt="Foto original" 
                          className="w-full h-full object-cover rounded-[1.5rem]"
                        />
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-[2rem]"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                    <Image className="h-8 w-8 text-blue-600" />
                    <span>{t('tryOnSection.steps.original')}</span>
                  </h3>
                  <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('tryOnSection.steps.originalDesc')}</p>
                </motion.div>
                
                {/* Signo + */}
                <motion.div
                  className="hidden lg:block"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_12px_40px_rgba(147,51,234,0.4),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 backdrop-blur-sm border border-white/20">
                    <Plus className="h-10 w-10 text-white drop-shadow-sm" />
                  </div>
                </motion.div>
                
                {/* Paso 2: Camisa 2 - Prenda Elegida */}
                <motion.div 
                  className="text-center group"
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -15,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <div className="relative">
                    <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-purple-100 rounded-[2rem] shadow-[inset_-2px_-2px_10px_rgba(147,51,234,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(147,51,234,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                      
                      <div className="relative w-full h-full rounded-[2rem] overflow-hidden m-2">
                        <img 
                          src="/images/camisa_2.jpg" 
                          alt="Prenda elegida" 
                          className="w-full h-full object-cover rounded-[1.5rem]"
                        />
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-[2rem]"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                    <Shirt className="h-8 w-8 text-purple-600" />
                    <span>{t('tryOnSection.steps.garment')}</span>
                  </h3>
                  <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('tryOnSection.steps.garmentDesc')}</p>
                </motion.div>
                
                {/* Signo = */}
                <motion.div
                  className="hidden lg:block"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 1.6 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(236,72,153,0.3),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_12px_40px_rgba(236,72,153,0.4),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 backdrop-blur-sm border border-white/20">
                    <Equal className="h-10 w-10 text-white drop-shadow-sm" />
                  </div>
                </motion.div>
                
                {/* Paso 3: Camisa 3 - Resultado Final */}
                <motion.div 
                  className="text-center group"
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 1.8 }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -15,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <div className="relative">
                    <div className="w-80 h-80 sm:w-96 sm:h-96 relative mb-8 mx-auto">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-100 rounded-[2rem] shadow-[inset_-2px_-2px_10px_rgba(34,197,94,0.1),inset_2px_2px_10px_rgba(255,255,255,0.8)] group-hover:shadow-[inset_-4px_-4px_20px_rgba(34,197,94,0.15),inset_4px_4px_20px_rgba(255,255,255,0.9)] transition-all duration-500"></div>
                      
                      <div className="relative w-full h-full rounded-[2rem] overflow-hidden m-2">
                        <img 
                          src="/images/camisa_3.jpg" 
                          alt="Resultado final" 
                          className="w-full h-full object-cover rounded-[1.5rem]"
                        />
                      </div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                        ✨ IA
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000 rounded-[2rem]"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-green-600 transition-colors duration-300 flex items-center justify-center space-x-2">
                    <Wand2 className="h-8 w-8 text-green-600" />
                    <span>{t('tryOnSection.steps.result')}</span>
                  </h3>
                  <p className="text-gray-600 max-w-sm text-lg group-hover:text-gray-700 transition-colors duration-300">{t('tryOnSection.steps.resultDesc')}</p>
                </motion.div>
              </div>
          </div>
        </section>

        {/* Cómo funciona - Timeline Layout */}
        <section id="como-funciona" className="w-full bg-gradient-to-br from-blue-50 via-white to-purple-50 border-t border-gray-100 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4 py-20">
            <div className="container mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  {t('howItWorksSection.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                {t('howItWorksSection.description')}
              </p>
              
              {/* Botones de Acción */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={scrollToDemo}
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <Sparkles className="h-5 w-5" />
                  <span>{t('howItWorksSection.ctaTryFree')}</span>
                </button>
              </div>
            </motion.div>

            {/* Timeline Layout */}
            <div className="max-w-6xl mx-auto">
              {/* Vendedores - Left Side */}
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex flex-col lg:flex-row items-center mb-20"
              >
                <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
                  <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden">
                    {/* Decorative background */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16"></div>
                    
                    <div className="relative">
                      <div className="flex items-center mb-6">
                        <div className="w-20 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                          <Store className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-3xl font-bold text-gray-900">{t('howItWorksSection.sellers.title')}</h3>
                          <p className="text-blue-600 font-medium">{t('howItWorksSection.sellers.subtitle')}</p>
                        </div>
                      </div>
                      
                      <p className="text-lg text-gray-600 mb-10">{t('howItWorksSection.sellers.intro')}</p>
                      
                      {/* Timeline Steps */}
                      <div className="space-y-8">
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">1</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.sellers.step1Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.sellers.step1Desc')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">2</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.sellers.step2Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.sellers.step2Desc')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">3</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.sellers.step3Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.sellers.step3Desc')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Visual for Vendedores */}
                <div className="lg:w-1/2 lg:pl-12">
                  <div className="relative">
                    <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
                      {/* Decorative elements */}
                      <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full"></div>
                      <div className="absolute bottom-4 left-4 w-16 h-16 bg-white/10 rounded-full"></div>
                      
                      <div className="text-center relative">
                        <div className="w-40 h-40 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl backdrop-blur-sm">
                          <Store className="h-20 w-20 text-white" />
                        </div>
                        <h4 className="text-2xl font-bold mb-3">{t('howItWorksSection.sellers.dashboardTitle')}</h4>
                        <p className="text-blue-100 text-lg mb-6">{t('howItWorksSection.sellers.dashboardDesc')}</p>
                        
                        {/* Feature highlights */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                            <div className="font-semibold mb-1">{t('howItWorksSection.sellers.analytics')}</div>
                            <div className="text-blue-100">{t('howItWorksSection.sellers.realtimeVisits')}</div>
                          </div>
                          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                            <div className="font-semibold mb-1">{t('howItWorksSection.sellers.links')}</div>
                            <div className="text-blue-100">{t('howItWorksSection.sellers.easyShare')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Usuarios - Right Side */}
              <motion.div 
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-col lg:flex-row-reverse items-center"
              >
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden">
                    {/* Decorative background */}
                    <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-green-100 to-blue-100 rounded-full -translate-y-16 -translate-x-16"></div>
                    
                    <div className="relative">
                      <div className="flex items-center mb-6">
                        <div className="w-20 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                          <User className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-3xl font-bold text-gray-900">{t('howItWorksSection.users.title')}</h3>
                          <p className="text-green-600 font-medium">{t('howItWorksSection.users.subtitle')}</p>
                        </div>
                      </div>
                      
                      <p className="text-lg text-gray-600 mb-10">{t('howItWorksSection.users.intro')}</p>
                      
                      {/* Timeline Steps */}
                      <div className="space-y-8">
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">1</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.users.step1Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.users.step1Desc')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">2</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.users.step2Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.users.step2Desc')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xl">3</span>
                            </div>
                          </div>
                          <div className="pt-2 text-left">
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{t('howItWorksSection.users.step3Title')}</h4>
                            <p className="text-gray-600 text-lg">{t('howItWorksSection.users.step3Desc')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Visual for Usuarios */}
                <div className="lg:w-1/2 lg:pr-12">
                  <div className="relative">
                    <div className="bg-gradient-to-br from-green-500 to-blue-600 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
                      {/* Decorative elements */}
                      <div className="absolute top-4 left-4 w-20 h-20 bg-white/10 rounded-full"></div>
                      <div className="absolute bottom-4 right-4 w-16 h-16 bg-white/10 rounded-full"></div>
                      
                      <div className="text-center relative">
                        <div className="w-40 h-40 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl backdrop-blur-sm">
                          <User className="h-20 w-20 text-white" />
                        </div>
                        <h4 className="text-2xl font-bold mb-3">{t('howItWorksSection.users.virtualTryOnTitle')}</h4>
                        <p className="text-green-100 text-lg mb-6">{t('howItWorksSection.users.virtualTryOnDesc')}</p>
                        
                        {/* Feature highlights */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                            <div className="font-semibold mb-1">{t('howItWorksSection.users.ai')}</div>
                            <div className="text-green-100">{t('howItWorksSection.users.autoGeneration')}</div>
                          </div>
                          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                            <div className="font-semibold mb-1">{t('howItWorksSection.users.fast')}</div>
                            <div className="text-green-100">{t('howItWorksSection.users.instantResults')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section id="demo-section" className="w-full py-20 bg-white rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto">
              <ImprovedDemo />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="planes" className="w-full py-20 bg-white rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {t('pricing.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('pricing.description')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Plan Gratuito */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 relative flex flex-col h-full"
              >
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.freePlan.title')}</h3>
                  <p className="text-gray-600 mb-6">{t('pricing.freePlan.subtitle')}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">{t('pricing.freePlan.price')}</span>
                    <span className="text-gray-600 text-xl">{t('pricing.freePlan.perMonth')}</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">{t('pricing.freePlan.features.catalog')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">{t('pricing.freePlan.features.photos')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">{t('pricing.freePlan.features.tokens')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">{t('pricing.freePlan.features.watermarks')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-gray-500 line-through">{t('pricing.freePlan.features.links')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-gray-500 line-through">{t('pricing.freePlan.features.analytics')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-gray-500 line-through">{t('pricing.freePlan.features.video')}</span>
                  </li>
                </ul>

                <button
                  onClick={redirectToRegister}
                  className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-300 mt-auto"
                >
                  {t('pricing.freePlan.cta')}
                </button>
              </motion.div>

              {/* Plan Profesional - Destacado */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-2xl p-8 relative transform scale-105 flex flex-col h-full"
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <motion.span 
                    className="bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 2, -2, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        rotate: [0, 10, -10, 0]
                      }}
                      transition={{ 
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Flame className="h-4 w-4 text-orange-600" />
                    </motion.div>
                    {t('pricing.proPlan.badge')}
                  </motion.span>
                </div>

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('pricing.proPlan.title')}</h3>
                  <p className="text-blue-100 mb-6">{t('pricing.proPlan.subtitle')}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-white">{t('pricing.proPlan.price')}</span>
                    <span className="text-blue-100 text-xl">{t('pricing.proPlan.perMonth')}</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.catalogs')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.images')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.tokens')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.links')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.analytics')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.watermarks')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.video')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.models')}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-white">{t('pricing.proPlan.features.features')}</span>
                  </li>
                </ul>

                <button
                  onClick={redirectToRegister}
                  className="w-full bg-white text-blue-600 py-3 px-6 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 mt-auto"
                >
                  {t('pricing.proPlan.cta')}
                </button>
              </motion.div>

            </div>

            {/* Información sobre tokens */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mt-8 text-center"
            >
              <p className="text-sm text-gray-500">
              * {t('pricing.tokensInfo')}
              </p>
            </motion.div>

            </div>
          </div>
        </section>

        {/* Virtual Models Section */}
        <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <motion.span 
                className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                {t('virtualModels.badge')}
              </motion.span>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {t('virtualModels.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
                {t('virtualModels.subtitle')}
              </p>
              <p className="text-lg text-gray-500 max-w-4xl mx-auto">
                {t('virtualModels.description')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="space-y-8"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('virtualModels.features.generate')}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Crea modelos únicos con IA
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      <Wand2 className="h-6 w-6 text-pink-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('virtualModels.features.customize')}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Personaliza el estilo y look
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      <Sparkles className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('virtualModels.features.professional')}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Resultados de calidad profesional
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      <Shirt className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('virtualModels.features.reuse')}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Úsalos en todos tus catálogos
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-xl text-center"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('virtualModels.benefits.title')}
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600 mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {t('virtualModels.benefits.cost')}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Ahorra en sesiones de fotos y modelos reales
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600 mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {t('virtualModels.benefits.availability')}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Genera modelos cuando los necesites
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600 mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {t('virtualModels.benefits.consistency')}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Mantén una imagen coherente en tu marca
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600 mr-4" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {t('virtualModels.benefits.flexibility')}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Adapta los modelos a tu estilo y necesidades
                      </p>
                    </div>
                  </div>
                </div>
                
                <motion.button
                  onClick={redirectToRegister}
                  className="w-full mt-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('virtualModels.cta')}
                </motion.button>
                
                <motion.button
                  onClick={() => setShowVideoModal(true)}
                  className="w-full mt-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Users className="h-5 w-5 mr-2" />
                  {t('virtualModels.viewDemo')}
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Sección de Preguntas Frecuentes */}
        <section className="w-full py-20 bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto max-w-4xl">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  {t('faq.title')}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {t('faq.description')}
                </p>
              </motion.div>

              <div className="space-y-4">
                {/* FAQ 1 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q1.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 1 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 1 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q1.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* FAQ 2 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q2.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 2 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 2 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q2.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* FAQ 3 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q3.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 3 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 3 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q3.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* FAQ 4 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 4 ? null : 4)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q4.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 4 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 4 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q4.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* FAQ 5 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 5 ? null : 5)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q5.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 5 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 5 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q5.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* FAQ 6 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 6 ? null : 6)}
                    className="w-full px-8 py-6 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('faq.q6.question')}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === 6 ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFAQ === 6 ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 text-left">
                      <p className="text-gray-600 leading-relaxed">
                        {t('faq.q6.answer')}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Sección de Contacto */}
        <section id="contacto" className="w-full py-20 bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl mx-2 sm:mx-4 my-8">
          <div className="w-full px-4">
            <div className="container mx-auto max-w-4xl">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  {t('contact.title')}
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  {t('contact.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
              >
                <ContactForm />
              </motion.div>
            </div>
          </div>
        </section>

      </div>

      {/* Footer - Fuera del contenedor principal para full width */}
        <footer className="w-full bg-gray-900 text-white">
          <div className="w-full px-4 py-16">
            <div className="container mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Logo y Descripción */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <img 
                    src="/logo192.png" 
                    alt="Luxify Logo" 
                    className="w-8 h-8"
                  />
                  <span className="text-2xl font-bold">Luxify</span>
                </div>
                <p className="text-gray-300 mb-6 max-w-md text-left">
                  {t('footer.description')}
                </p>
              </div>

              {/* Enlaces Rápidos */}
              <div>
                <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={redirectToRegister}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {t('footer.register')}
                    </button>
                  </li>
                  <li>
                    {loading ? (
                      <span className="text-gray-400">{t('footer.verifying')}</span>
                    ) : user ? (
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-300 hover:text-white transition-colors"
                      >
                        {t('footer.dashboard')}
                      </button>
                    ) : (
                      <button
                        onClick={redirectToLogin}
                        className="text-gray-300 hover:text-white transition-colors"
                      >
                        {t('footer.signIn')}
                      </button>
                    )}
                  </li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-lg font-semibold mb-4">{t('footer.legal')}</h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => navigate('/tos')}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {t('footer.terms')}
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/privacy')}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {t('footer.privacy')}
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => scrollToSection('contacto', 'rgba(59, 130, 246, 0.3)')}
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {t('footer.contact')}
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            {/* Línea divisoria */}
            <div className="border-t border-gray-700 mt-12 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-gray-400 text-sm mb-4 md:mb-0">
                  {t('footer.copyright')}
                </p>
                <div className="flex items-center gap-6">
                  <a
                    href="https://www.instagram.com/luxify.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-400 hover:text-pink-400 transition-colors group"
                  >
                    <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">@luxify.app</span>
                  </a>
                  <span className="text-sm text-gray-400">{t('footer.madeWithLove')}</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </footer>

      {/* Modal de video demo */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoId="1I9gEcnaNFmJzAUuJaVsU0fGcU3a2Rsk1"
        title={t('virtualModels.viewDemo')}
      />
    </div>
  );
};

export default LandingPage;
